import { ChangeEvent, FC, useRef, useState } from "react";
import firebase from "firebase/compat/app";
import { v4 as uuidv4 } from "uuid";
import { HiOutlineUpload } from "react-icons/hi";
import { useTranslation } from "react-i18next";

import { Button } from "../../Button/Button";
import { Order } from "../../../models";
import { firestore, storage } from "../../../firebaseConfig";
import { Timestamp } from "../../../types";
import { IOrderAttachment } from "../../../types/order/IOrder";
import { useUserSession } from "../../../context/UserContext";

interface OrderAttachmentUploadProps {
  order: Order;
  workspaceId: string;
  onUploaded?: (attachments: IOrderAttachment[]) => void;
}

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  "image/*",
  "application/pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
];

const sanitizeFileName = (fileName: string) =>
  fileName
    .replace(/[^\w.\-() ]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);

const getContentType = (file: File) => {
  if (file.type) return file.type;

  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension && ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)) {
    return `image/${extension === "jpg" ? "jpeg" : extension}`;
  }

  if (extension === "pdf") return "application/pdf";
  return "application/octet-stream";
};

export const OrderAttachmentUpload: FC<OrderAttachmentUploadProps> = ({
  order,
  workspaceId,
  onUploaded,
}) => {
  const { t } = useTranslation(["common", "orders"]);
  const { userSession } = useUserSession();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (!files.length) return;

    setIsUploading(true);
    setError(null);

    try {
      const oversizedFile = files.find((file) => file.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFile) {
        throw new Error(
          t("orders:attachments.file_too_large", { fileName: oversizedFile.name }),
        );
      }

      const uploadedAttachments: IOrderAttachment[] = [];

      for (const file of files) {
        const attachmentId = uuidv4();
        const safeFileName = sanitizeFileName(file.name);
        const storagePath = `${workspaceId}/orders/${order.id}/routes/web-attachments/${attachmentId}-${safeFileName}`;
        const fileRef = storage.ref(storagePath);
        const contentType = getContentType(file);

        await fileRef.put(file, {
          contentType,
          customMetadata: {
            orderId: order.id,
            workspaceId,
            uploadedBy: userSession?.user.email ?? "",
          },
        });

        const url = await fileRef.getDownloadURL();
        uploadedAttachments.push({
          id: attachmentId,
          name: file.name,
          url,
          storagePath,
          contentType,
          size: file.size,
          uploadedAt: Timestamp.now(),
          uploadedBy: userSession?.user.email ?? null,
        });
      }

      await firestore.collection(Order.collectionName).doc(order.id).update({
        attachments: firebase.firestore.FieldValue.arrayUnion(...uploadedAttachments),
        lastUpdated: Timestamp.now(),
      });

      onUploaded?.(uploadedAttachments);
    } catch (uploadError) {
      console.error("Failed to upload order attachments:", uploadError);
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : t("orders:attachments.upload_failed"),
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES.join(",")}
        className="hidden"
        onChange={uploadFiles}
      />
      <Button
        variant="outline"
        size="sm"
        icon={<HiOutlineUpload className="h-4 w-4" />}
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
      >
        {isUploading
          ? t("orders:attachments.uploading")
          : t("orders:attachments.upload")}
      </Button>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t("orders:attachments.max_file_size")}
      </p>
      {error && (
        <p className="max-w-xs text-right text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};
